import { css } from '@emotion/react';
import { faEnvelope } from '@fortawesome/pro-regular-svg-icons/faEnvelope';
import { type FC } from 'react';
import { ButtonStyle } from '~/_shared/baseComponents/buttons/button/button.types';
import { CheckboxComponent } from '~/_shared/baseComponents/checkbox';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { Stack } from '~/_shared/baseComponents/layout/Stack.componment';
import { FormErrorMessageComponent } from '~/_shared/components/formErrorMessage/formErrorMessage.component';
import { TermsAndPrivacyNoticeComponent } from '~/_shared/components/termsAndPrivacyNotice/termsAndPrivacyNotice.component';
import { CONFIG } from '~/_shared/constants/config';
import type { Theme } from '~/_shared/themes/theme.model';
import { useTranslation } from '~/_shared/utils/hooks';
import { SsoButtonsComponent } from '~/authorization/loginPage/sso/ssoButtons.component';
import { ITEMS_GAP } from '~/authorization/registerPage/form/registerForm.component';
import { authorizationPagesErrorStyle } from '../../authorizationPages.styles';
import { AuthorizationButtonComponent } from '../../submitButton/authorizationSubmitButton.component';
import { RegisterFormFooter } from './registerFormFooter.component';

const privacyTextStyle = css({
  fontSize: 16,
  marginLeft: 8,
});

const columnStyle = css({
  flex: 1,
  minWidth: 0,
});

const orDividerWrapperStyle = css({
  cursor: 'default',
  display: 'flex',
  alignItems: 'center',
});

const orDividerLineStyle = (theme: Theme) => css({
  flex: 1,
  height: 1,
  backgroundColor: theme.lineColors.basePrimary,
});

const orDividerTextStyle = (theme: Theme) => css({
  padding: '0 12px',
  fontSize: 12,
  color: theme.textColors.secondary,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  whiteSpace: 'nowrap',
  textTransform: 'uppercase',
});

export type FormField = {
  value: string;
  errors: ReadonlyArray<string>;
  onBlur: () => void;
  onChange: (value: string) => void;
};

type RegisterFormOptionsViewProps = Readonly<{
  email: FormField;
  isLoading: boolean;
  terms: Readonly<{ accepted: boolean; showError: boolean; onChange: (checked: boolean) => void }>;
  sso: Readonly<{ onGoogleLogin: () => void; onMicrosoftLogin: () => void }>;
  onShowEmailForm: () => void;
}>;

export const RegisterFormOptionsView: FC<RegisterFormOptionsViewProps> = (props) => {
  const [t] = useTranslation();

  return (
    <Stack gap={ITEMS_GAP}>
      <div>
        <CheckboxComponent
          checkedSetter={(event) => {
            props.terms.onChange(event.target.checked);
          }}
          isChecked={props.terms.accepted}
          text={<TermsAndPrivacyNoticeComponent />}
          textStyle={privacyTextStyle}
        />

        {props.terms.showError && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            isSingleLine={false}
            messages={[t('signup.agreement.warning')]}
          />
        )}
      </div>

      {CONFIG.ENABLE_SSO_LOGIN && (
        <div>
          <SsoButtonsComponent
            isDisabled={props.isLoading}
            onGoogleLogin={props.sso.onGoogleLogin}
            onMicrosoftLogin={props.sso.onMicrosoftLogin}
          />
        </div>
      )}

      <div css={orDividerWrapperStyle}>
        <div css={orDividerLineStyle} />
        <div css={orDividerTextStyle}>{t('or')}</div>
        <div css={orDividerLineStyle} />
      </div>

      <div css={columnStyle}>
        <TextInputAuthComponent
          hasError={props.email.errors.length > 0}
          icon={faEnvelope}
          isDisabled={props.isLoading}
          label={t('Email')}
          name="email"
          onBlur={props.email.onBlur}
          onChange={props.email.onChange}
          placeholder={t('Email')}
          value={props.email.value}
        />

        {props.email.errors.length > 0 && (
          <FormErrorMessageComponent
            css={authorizationPagesErrorStyle}
            isSingleLine={false}
            messages={props.email.errors}
          />
        )}
      </div>

      <AuthorizationButtonComponent
        buttonStyle={ButtonStyle.Primary}
        isDisabled={props.isLoading}
        isLoading={false}
        onClick={props.onShowEmailForm}
      >
        {t('signup.email.continueWithEmail')}
      </AuthorizationButtonComponent>

      <RegisterFormFooter />
    </Stack>
  );
};
